import React, { ReactNode, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/services/auth/AuthContext";
import {
  QueryKeyArray,
  createAuthRetryHandler,
  useAPIHelper as fetchAPIData
} from "@/services/api/hook";
import { parseChildAccountIds } from "@/services/auth/activeAccount";
import {
  useReturnHomeOnLinkedAccountRefusal,
  useSwitchAccount
} from "@/services/auth/useSwitchAccount";
import { AccountData } from "@/entities/accounts/types";
import ScreenLoader from "@/components/shared/ScreenLoader";

/**
 * Reads the home account's `childAccountIds` from GET /accounts — always with
 * the home tenant, whichever account is active — and reports it to the auth
 * context. A missing or malformed list counts as "no children"; a failed first read
 * falls back to the home account without dropping the persisted choice.
 */
const useChildAccountIds = (): void => {
  const {
    isAuthenticated,
    token,
    homeTenantId,
    tokenExpiry,
    login,
    reportChildAccountIds
  } = useAuth();

  const homeAuthRef = useRef({ token, tenantId: homeTenantId, tokenExpiry });
  homeAuthRef.current = { token, tenantId: homeTenantId, tokenExpiry };

  const queryKey: QueryKeyArray = ["accounts", {}, { tenantId: homeTenantId }];

  const { data, isError } = useQuery<
    AccountData,
    Error,
    AccountData,
    QueryKeyArray
  >({
    queryKey,
    enabled: isAuthenticated && !!homeTenantId,
    queryFn: ({ queryKey: key, signal }) =>
      fetchAPIData(homeAuthRef.current, key, signal),
    retry: createAuthRetryHandler(login)
  });

  const { activeAccountId, isLinkedAccount, switchToHome } = useSwitchAccount();
  const activeAccountRef = useRef({
    activeAccountId,
    isLinkedAccount,
    switchToHome
  });
  activeAccountRef.current = { activeAccountId, isLinkedAccount, switchToHome };

  // A failed refetch keeps the last list that loaded, and a list that no
  // longer names the active child returns home through a full switch: either
  // way the active account never changes without the cache clearing.
  const hasLoadedList = data !== undefined;
  useEffect(() => {
    if (hasLoadedList) {
      const loadedChildAccountIds = parseChildAccountIds(data?.childAccountIds);
      const active = activeAccountRef.current;
      if (
        active.isLinkedAccount &&
        active.activeAccountId &&
        !loadedChildAccountIds.includes(active.activeAccountId)
      ) {
        active.switchToHome();
      }
      reportChildAccountIds(loadedChildAccountIds);
    } else if (isError) {
      reportChildAccountIds([], true);
    }
  }, [data?.childAccountIds, hasLoadedList, isError, reportChildAccountIds]);
};

// Holds the app back while a persisted account choice is checked against the
// home account's children, so nothing renders or fetches under the wrong
// account first.
export const ActiveAccountGate: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const { isAuthenticated, isResolvingAccount } = useAuth();
  useChildAccountIds();
  useReturnHomeOnLinkedAccountRefusal();

  if (isAuthenticated && isResolvingAccount) {
    return <ScreenLoader />;
  }

  return <>{children}</>;
};
